import { type OptimizerPort } from '@analytical-alley/opt-wasm';
import { type OptimisationBase } from '@/optimizer';

// Helper to handle boundary cases
const getBudgetResult = (
    adjustedBudget: number,
    errorMessage?: string,
): { budget: number; budgetError: string | undefined } => ({
    budget: adjustedBudget,
    budgetError: errorMessage,
});

/**
 * Validates and adjusts a given budget based on defined constraints and optimization logic.
 *
 * @param {number | undefined} budget - The budget provided by the user. Can be undefined, which triggers the use of the default budget.
 * @param {OptimizerPort} optimizerService - An instance of the optimizer service that provides boundary constraints and refinement logic.
 * @param {number} defaultBudget - The default budget to use if no budget is provided.
 * @param {OptimisationBase} optimizationBase - The optimization mode (budget, media_kpi, or total_kpi).
 * @returns {Object} An object containing the adjusted budget and an optional error message if corrections were applied.
 * @property {number} budget - The validated or adjusted budget value.
 * @property {string | undefined} budgetError - A descriptive error message if the budget was out of bounds and adjusted; undefined otherwise.
 */
export const checkBudget = async (
    budget: number | undefined,
    optimizerService: OptimizerPort,
    defaultBudget: number,
    optimizationBase: OptimisationBase = 'budget',
): Promise<{ budget: number; budgetError: string | undefined }> => {
    if (!budget) {
        return { budget: defaultBudget, budgetError: undefined };
    }

    if (optimizationBase !== 'budget') {
        return getBudgetResult(budget);
    }

    const { lowestBudget, highestBudget, refineSpend } = optimizerService;

    if (budget < lowestBudget) {
        return getBudgetResult(
            lowestBudget,
            `Entered budget was too low. Changed it to lowest possible which is ${lowestBudget}.`,
        );
    }

    if (budget > highestBudget) {
        return getBudgetResult(
            highestBudget,
            `Entered budget was too high. Changed it to highest possible which is ${highestBudget}.`,
        );
    }

    return getBudgetResult(await refineSpend(budget));
};
